// frontend/src/modules/projects/components/TimerWidget.tsx
import * as React from 'react';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../../../components/ui/select';
import { useApi } from '../../../design-system/useApi';
import { usePermission } from '../../../core/auth/usePermission';
import { statusPill, statusDot } from '../../../design-system/visual';
import { projectsApi } from '../api';
import type { Project, Task, TimeEntry } from '../types';

interface Props {
  onChange?: () => void;
}

function formatDuration(start: string): string {
  const ms = Date.now() - new Date(start).getTime();
  const totalMin = Math.floor(ms / 60000);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

export function TimerWidget({ onChange }: Props) {
  const openQ = useApi<{ entry: TimeEntry | null }>('/api/projects/time-entries/me/open');
  // GET /projects requires projects.view — skip the fetch (it would 403) when
  // the user lacks it, and show a friendly note instead of an empty picker.
  const canViewProjects = usePermission('projects.view');
  const projectsQ = useApi<{ projects: Project[] }>(canViewProjects ? '/api/projects/projects?status=active' : null, [canViewProjects]);
  const [selectedProjectId, setSelectedProjectId] = React.useState('');
  const [selectedTaskId, setSelectedTaskId] = React.useState('');
  const tasksQ = useApi<{ tasks: Task[] }>(selectedProjectId ? `/api/projects/tasks?project_id=${selectedProjectId}` : null, [selectedProjectId]);
  const [notes, setNotes] = React.useState('');
  const [busy, setBusy] = React.useState(false);

  // Poll the open entry every 30s. 404 = no employee record (`not_employee`)
  // — permanent for this session, so stop polling rather than repeat a 404.
  const notEmployee = openQ.status === 404;
  React.useEffect(() => {
    if (notEmployee) return;
    const id = setInterval(() => openQ.reload(), 30_000);
    return () => clearInterval(id);
  }, [notEmployee]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-render every second so the duration counter ticks
  const [, force] = React.useState(0);
  React.useEffect(() => {
    if (!openQ.data?.entry) return;
    const id = setInterval(() => force((x) => x + 1), 1000);
    return () => clearInterval(id);
  }, [openQ.data?.entry?.id]);

  const running = openQ.data?.entry ?? null;

  async function start() {
    if (!selectedProjectId) { toast.error('Pick a project'); return; }
    setBusy(true);
    try {
      await projectsApi.startTimer({
        project_id: selectedProjectId,
        task_id: selectedTaskId || null,
        notes: notes || null,
      });
      toast.success('Timer started');
      setNotes(''); openQ.reload(); onChange?.();
    } catch (err: any) {
      toast.error('Could not start', { description: err.message });
    } finally { setBusy(false); }
  }

  async function stop() {
    setBusy(true);
    try {
      await projectsApi.stopTimer();
      toast.success('Timer stopped');
      openQ.reload(); onChange?.();
    } catch (err: any) {
      toast.error('Could not stop', { description: err.message });
    } finally { setBusy(false); }
  }

  if (openQ.loading) return <div className="text-xs text-muted-foreground border rounded-md p-4 bg-card">Checking timer…</div>;

  if (running) {
    return (
      <div className="border rounded-md p-4 bg-card flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <span className={statusPill('success')}><span className={statusDot('success')} />Running</span>
          <div>
            <div className="text-sm font-medium">{running.notes ?? '(no notes)'}</div>
            <div className="text-xs text-muted-foreground">Started {new Date(running.start_at).toLocaleTimeString()}</div>
          </div>
          <div className="ml-4 font-mono text-2xl tabular-nums">{formatDuration(running.start_at)}</div>
        </div>
        <Button disabled={busy} onClick={stop}>{busy ? 'Stopping…' : 'Stop'}</Button>
      </div>
    );
  }

  if (!canViewProjects) {
    // Timers must attach to a project; without projects.view the user can't
    // pick one, so starting a timer here is impossible by design.
    return (
      <div className="text-xs text-muted-foreground border rounded-md p-4 bg-card">
        Starting a timer needs project access — ask a workspace admin to grant it.
      </div>
    );
  }

  const projects = projectsQ.data?.projects ?? [];
  const tasks = tasksQ.data?.tasks ?? [];

  return (
    <div className="border rounded-md p-4 bg-card grid gap-3">
      <div className="text-sm font-medium">Start a timer</div>
      <div className="grid grid-cols-3 gap-2">
        <Select value={selectedProjectId} onValueChange={(v) => { setSelectedProjectId(v); setSelectedTaskId(''); }}>
          <SelectTrigger><SelectValue placeholder="Project" /></SelectTrigger>
          <SelectContent>
            {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={selectedTaskId} onValueChange={setSelectedTaskId} disabled={!selectedProjectId}>
          <SelectTrigger><SelectValue placeholder={selectedProjectId ? 'Task (optional)' : 'Pick a project first'} /></SelectTrigger>
          <SelectContent>
            {tasks.map((t) => <SelectItem key={t.id} value={t.id}>{t.title}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input placeholder="Notes (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} />
      </div>
      <div className="flex justify-end">
        <Button disabled={busy || !selectedProjectId} onClick={start}>{busy ? 'Starting…' : 'Start'}</Button>
      </div>
    </div>
  );
}
